/**
 * Create a promoted-only post: text, an image or a video (uploaded or from the media library)
 * and an optional card (website, X lead form, or app store button) — or a CAROUSEL: 2–6
 * swipeable images or videos with one website or app destination.
 *
 * A post carries EITHER media OR a card (the card holds the media). The parts are created in
 * order — media → card → post — and each one that succeeded is kept, so "Try again" after a
 * failure only redoes what is missing.
 *
 * X's sizes: images 1.91:1 (1200×628, at least 800×418) or 1:1 (1200×1200, at least 800×800), JPEG /
 * PNG up to 5 MB; videos 16:9 (1920×1080) or 1:1 (1080×1080), MP4 / MOV, up to 2 min 20 s. In a
 * carousel every card has the same shape, and images and videos are not mixed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Film, GalleryHorizontal, ImagePlus, Loader2, RefreshCw, Send, Trash2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { XSelect } from './XSelect';
import { errMsg, xAdsService, type XAppButton, type XLeadForm, type XMedia, type XPost } from '@/services/x-ads.service';

type CardKind = 'none' | 'website' | 'lead' | 'app';
type Part = 'media' | 'card' | 'post';
type Format = 'single' | 'carousel';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const CAROUSEL_MIN = 2;
const CAROUSEL_MAX = 6;
const IMAGE_HINT = 'Images: 1.91:1 (1200×628) or 1:1 (1200×1200), JPEG / PNG, 5 MB';
const VIDEO_HINT = 'Videos: 16:9 (1920×1080) or 1:1 (1080×1080), MP4 / MOV, 100 MB, up to 2 min 20 s';
const APP_BUTTONS: Array<{ value: XAppButton; label: string }> = [
  { value: 'INSTALL', label: 'Install' }, { value: 'OPEN', label: 'Open' }, { value: 'PLAY', label: 'Play' }, { value: 'SHOP', label: 'Shop' },
  { value: 'BOOK', label: 'Book' }, { value: 'CONNECT', label: 'Connect' }, { value: 'ORDER', label: 'Order' },
];
const isVideoFile = (f: File | null | undefined) => !!f && VIDEO_TYPES.includes(f.type);
const isVideo = (m?: XMedia) => m?.mediaType === 'VIDEO';
const seconds = (ms?: number) => (ms ? `${Math.round(ms / 1000)}s` : '');
const fileProblem = (f: File): string | null => {
  if (isVideoFile(f)) return f.size > MAX_VIDEO_BYTES ? `"${f.name}": videos must be MP4 or MOV, 100 MB or smaller.` : null;
  return !IMAGE_TYPES.includes(f.type) || f.size > MAX_IMAGE_BYTES ? `"${f.name}": images must be JPEG, PNG, GIF or WEBP, 5 MB or smaller.` : null;
};

/** One carousel card: a file to upload or a library item; `key` once it is on X; `ratio` once its shape is known. */
interface Slide { id: string; kind: 'image' | 'video'; file?: File; media?: XMedia; key?: string; preview?: string; ratio?: number }
let slideSeq = 0;

export function PostComposer({ uid, onCreated }: { uid: string; onCreated: (post: XPost) => void }) {
  const [format, setFormat] = useState<Format>('single');
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [libraryKey, setLibraryKey] = useState('');
  const [library, setLibrary] = useState<XMedia[] | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [cardKind, setCardKind] = useState<CardKind>('none');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('https://');
  const [leadFormId, setLeadFormId] = useState('');
  const [leadForms, setLeadForms] = useState<XLeadForm[] | null>(null);
  const [appStore, setAppStore] = useState<'ios' | 'android'>('ios');
  const [appId, setAppId] = useState('');
  const [appCountry, setAppCountry] = useState('AE');
  const [appButton, setAppButton] = useState<XAppButton>('INSTALL');
  // What already exists on X for this draft.
  const [uploaded, setUploaded] = useState<{ file: File; mediaKey: string } | null>(null);
  const [card, setCard] = useState<{ sig: string; cardUri: string } | null>(null);
  const [busy, setBusy] = useState<Part | null>(null);
  const [progress, setProgress] = useState('');
  const [failed, setFailed] = useState<{ part: Part; message: string } | null>(null);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  // Object URLs of carousel files are freed when the composer closes.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  useEffect(() => () => { slidesRef.current.forEach((sl) => sl.file && sl.preview && URL.revokeObjectURL(sl.preview)); }, []);

  useEffect(() => {
    if (cardKind !== 'lead' || leadForms) return;
    xAdsService.leadForms(uid).then(setLeadForms).catch(() => setLeadForms([]));
  }, [cardKind, leadForms, uid]);

  const loadLibrary = () => { if (!library) xAdsService.media(uid).then(setLibrary).catch(() => setLibrary([])); };
  const switchFormat = (f: Format) => {
    setFormat(f);
    setFailed(null);
    setCard(null);
    // A carousel opens a website or an app; it has no "no card" or lead-form variant.
    if (f === 'carousel' && (cardKind === 'none' || cardKind === 'lead')) setCardKind('website');
  };

  // ── Single ────────────────────────────────────────────────────────────────
  const picked = library?.find((m) => m.mediaKey === libraryKey);
  const mediaKey = (uploaded && uploaded.file === file ? uploaded.mediaKey : '') || (!file ? libraryKey : '');
  const hasMedia = format === 'carousel' ? slides.length > 0 : !!file || !!libraryKey;
  const video = format === 'carousel' ? slides[0]?.kind === 'video' : isVideoFile(file) || (!file && isVideo(picked));

  // ── Carousel ──────────────────────────────────────────────────────────────
  const addSlides = (next: Slide[]) => {
    setFailed(null);
    setCard(null);
    setSlides((cur) => {
      const room = CAROUSEL_MAX - cur.length;
      if (next.length > room) toast.error(`A carousel takes ${CAROUSEL_MAX} cards at most.`);
      return [...cur, ...next.slice(0, Math.max(0, room))];
    });
  };
  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files);
    const bad = list.map(fileProblem).find(Boolean);
    if (bad) { toast.error(bad); return; }
    addSlides(list.map((f) => ({ id: `s${++slideSeq}`, kind: isVideoFile(f) ? 'video' : 'image', file: f, preview: URL.createObjectURL(f) })));
  };
  const removeSlide = (id: string) => {
    setCard(null);
    setSlides((cur) => {
      const gone = cur.find((sl) => sl.id === id);
      if (gone?.file && gone.preview) URL.revokeObjectURL(gone.preview);
      return cur.filter((sl) => sl.id !== id);
    });
  };
  const toggleLibrarySlide = (m: XMedia) => {
    const existing = slides.find((sl) => sl.media?.mediaKey === m.mediaKey);
    if (existing) { removeSlide(existing.id); return; }
    addSlides([{ id: `s${++slideSeq}`, kind: isVideo(m) ? 'video' : 'image', media: m, key: m.mediaKey, preview: isVideo(m) ? m.posterUrl : m.mediaUrl }]);
  };
  const moveSlide = (id: string, by: -1 | 1) => {
    setCard(null);
    setSlides((cur) => {
      const i = cur.findIndex((sl) => sl.id === id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const setRatio = (id: string, w: number, h: number) => {
    if (!w || !h) return;
    setSlides((cur) => cur.map((sl) => (sl.id === id && !sl.ratio ? { ...sl, ratio: w / h } : sl)));
  };
  const shapes = slides.map((sl) => sl.ratio).filter((r): r is number => !!r);
  const mixedShapes = shapes.length > 1 && Math.max(...shapes) / Math.min(...shapes) > 1.05;
  const mixedKinds = new Set(slides.map((sl) => sl.kind)).size > 1;

  const cardSig = JSON.stringify([format, slides.map((sl) => sl.id), cardKind, title.trim(), url.trim(), leadFormId, appStore, appId.trim(), appCountry, appButton, name.trim()]);

  const problem = (): string | null => {
    if (text.length > 280) return 'Post text must be 280 characters or fewer.';
    if (format === 'carousel') {
      if (slides.length < CAROUSEL_MIN) return `A carousel needs ${CAROUSEL_MIN} to ${CAROUSEL_MAX} images or videos.`;
      if (mixedKinds) return 'A carousel is all images or all videos — do not mix them.';
      if (mixedShapes) return 'Every card of a carousel needs the same shape: all 1:1 (square) or all 1.91:1 (landscape).';
      if (cardKind !== 'website' && cardKind !== 'app') return 'A carousel opens a website or an app.';
    } else {
      if (!text.trim() && !hasMedia) return 'Write the post text or add an image or a video.';
      if (file) { const p = fileProblem(file); if (p) return p; }
      if (cardKind !== 'none' && !hasMedia) return 'A card needs an image or a video.';
    }
    if (cardKind === 'app') {
      const id = appId.trim();
      if (appStore === 'ios' && !/^\d{6,12}$/.test(id)) return 'The App Store id is the number in the app\'s App Store link, e.g. 333903271.';
      if (appStore === 'android' && !/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)+$/.test(id)) return 'The Google Play package name looks like com.company.app.';
      if (!/^[A-Za-z]{2}$/.test(appCountry.trim())) return 'The app store country is a 2-letter code, e.g. AE.';
    } else if (cardKind !== 'none') {
      if (!title.trim()) return 'Give the card a headline.';
      if (title.trim().length > 70) return 'The card headline must be 70 characters or fewer.';
      if (cardKind === 'website' && !/^https:\/\/[^\s/$.?#].[^\s]*$/i.test(url.trim())) return 'The website must be a full https:// address.';
      if (cardKind === 'lead' && !leadFormId) return 'Pick the lead form the card opens.';
    }
    return null;
  };

  const pickFile = (f: File | null) => { setFile(f); if (f) setLibraryKey(''); setFailed(null); };

  /** Uploads the carousel files not yet on X, keeping each key, and returns all keys in order. */
  const uploadSlides = async (): Promise<string[]> => {
    const keys: string[] = [];
    for (let i = 0; i < slides.length; i++) {
      const sl = slides[i];
      if (sl.key) { keys.push(sl.key); continue; }
      setProgress(`Uploading card ${i + 1} of ${slides.length}${sl.kind === 'video' ? ' — X processes videos, this can take a minute' : ''}…`);
      const media = sl.kind === 'video' ? await xAdsService.uploadVideo(uid, sl.file!) : await xAdsService.uploadImage(uid, sl.file!);
      keys.push(media.mediaKey!);
      setSlides((cur) => cur.map((x) => (x.id === sl.id ? { ...x, key: media.mediaKey } : x)));
    }
    return keys;
  };

  const submit = async () => {
    const p = problem();
    if (p) { toast.error(p); return; }
    setFailed(null);
    let part: Part = 'media';
    try {
      let key = mediaKey;
      let keys: string[] = [];
      setBusy('media');
      if (format === 'carousel') keys = await uploadSlides();
      else if (file && !key) {
        setProgress(isVideoFile(file) ? 'Uploading the video to X — X processes it, this can take a minute or two…' : 'Uploading the image to X…');
        key = (isVideoFile(file) ? await xAdsService.uploadVideo(uid, file) : await xAdsService.uploadImage(uid, file)).mediaKey!;
        setUploaded({ file, mediaKey: key });
      }
      let cardUri = '';
      if (cardKind !== 'none') {
        part = 'card';
        if (card?.sig === cardSig) cardUri = card.cardUri;
        else {
          setBusy('card');
          setProgress('Creating the card…');
          const created = await xAdsService.createCard(uid, {
            name: (name.trim() || title.trim() || `App card ${appId.trim()}`).slice(0, 80),
            ...(format === 'carousel' ? { mediaKeys: keys } : { mediaKey: key }),
            ...(cardKind === 'app'
              ? { app: { store: appStore, id: appId.trim(), countryCode: appCountry.trim().toUpperCase(), button: appButton } }
              : { title: title.trim(), ...(cardKind === 'website' ? { websiteUrl: url.trim() } : { leadFormId }) }),
          });
          if (!created.cardUri) throw new Error('X did not return the card.');
          cardUri = created.cardUri;
          setCard({ sig: cardSig, cardUri });
        }
      }
      part = 'post';
      setBusy('post');
      setProgress('Creating the post…');
      const post = await xAdsService.createPost(uid, { text: text.trim() || undefined, name: name.trim() || undefined, ...(cardUri ? { cardUri } : key ? { mediaKeys: [key] } : {}) });
      toast.success(format === 'carousel' ? 'Carousel post created on X' : 'Post created on X');
      onCreated(post);
      slides.forEach((sl) => sl.file && sl.preview && URL.revokeObjectURL(sl.preview));
      setText(''); setName(''); setFile(null); setLibraryKey(''); setSlides([]); setCardKind(format === 'carousel' ? 'website' : 'none');
      setTitle(''); setUrl('https://'); setLeadFormId(''); setAppId(''); setUploaded(null); setCard(null);
    } catch (e) {
      setFailed({ part, message: errMsg(e) });
    } finally {
      setBusy(null);
      setProgress('');
    }
  };

  const steps: Array<{ part: Part; label: string; done: boolean; show: boolean }> = [
    { part: 'media', label: isVideoFile(file) ? 'Video uploaded' : 'Image uploaded', done: !!uploaded && uploaded.file === file, show: format === 'single' && !!file },
    { part: 'media', label: `${slides.filter((sl) => sl.key).length} of ${slides.length} cards on X`, done: slides.length > 0 && slides.every((sl) => sl.key), show: format === 'carousel' && slides.some((sl) => sl.file) },
    { part: 'card', label: 'Card created', done: card?.sig === cardSig, show: cardKind !== 'none' },
  ];
  const uploadLabel = 'inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-surface-alt';
  const cardOptions = format === 'carousel'
    ? [{ value: 'website', label: 'Website — headline and link under the cards' }, { value: 'app', label: 'App — install / open button' }]
    : [
      { value: 'none', label: video ? 'No card — video only' : 'No card — image or text only' }, { value: 'website', label: 'Website card' },
      { value: 'lead', label: 'Lead form card' }, { value: 'app', label: 'App card (install / open button)' },
    ];

  return <div className="grid gap-x-6 lg:grid-cols-[minmax(0,1fr)_260px]">
    <div>
      <div className="xa-subtabs" role="group" aria-label="Post format">
        <button type="button" aria-pressed={format === 'single'} onClick={() => switchFormat('single')}><ImagePlus size={14} />Single image or video</button>
        <button type="button" aria-pressed={format === 'carousel'} onClick={() => switchFormat('carousel')}><GalleryHorizontal size={14} />Carousel (2–6 cards)</button>
      </div>
      <FormField label="Post text" htmlFor="xp-text" hint={`${text.length}/280`}>
        <Textarea id="xp-text" rows={4} maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder="What should people see in their timeline?" />
      </FormField>
      <FormField label="Internal name" htmlFor="xp-name" hint="Optional, only you see it">
        <Input id="xp-name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>

      {format === 'single' && <FormField label="Image or video" hint={`${IMAGE_HINT} · ${VIDEO_HINT}`} hintBelow>
        <div className="flex flex-wrap items-center gap-2">
          <label className={uploadLabel}>
            <ImagePlus size={15} />{file && !isVideoFile(file) ? 'Replace image' : 'Upload image'}
            <input type="file" accept={IMAGE_TYPES.join(',')} className="sr-only" onChange={(e) => { pickFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
          </label>
          <label className={uploadLabel}>
            <Film size={15} />{isVideoFile(file) ? 'Replace video' : 'Upload video'}
            <input type="file" accept={VIDEO_TYPES.join(',')} className="sr-only" onChange={(e) => { pickFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
          </label>
          {!library && <Button variant="ghost" size="sm" onClick={loadLibrary}>Use one from the media library</Button>}
          {hasMedia && <Button variant="ghost" size="sm" onClick={() => { pickFile(null); setLibraryKey(''); }}>Remove</Button>}
        </div>
        {library && <LibraryGrid library={library} isOn={(m) => libraryKey === m.mediaKey} onPick={(m) => { setLibraryKey(m.mediaKey!); setFile(null); }} />}
        {isVideoFile(file) && <p className="xa-muted mt-1">X processes the video after upload; that can take a minute or two.</p>}
      </FormField>}

      {format === 'carousel' && <FormField label={`Carousel cards (${slides.length}/${CAROUSEL_MAX})`} hintBelow
        hint="All images or all videos, all the same shape — 1:1 (1200×1200) or 1.91:1 (1200×628); videos 16:9 or 1:1">
        <div className="flex flex-wrap items-center gap-2">
          <label className={`${uploadLabel} ${slides.length >= CAROUSEL_MAX ? 'pointer-events-none opacity-50' : ''}`}>
            <ImagePlus size={15} />Upload images or videos
            <input type="file" multiple accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(',')} className="sr-only" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
          {!library && <Button variant="ghost" size="sm" onClick={loadLibrary}>Add from the media library</Button>}
        </div>
        {library && <LibraryGrid library={library} isOn={(m) => slides.some((sl) => sl.media?.mediaKey === m.mediaKey)} onPick={toggleLibrarySlide} />}
        {slides.length > 0 && <ol className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label="Carousel cards in order">
          {slides.map((sl, i) => <li key={sl.id} className="overflow-hidden rounded-lg border border-border bg-surface">
            <div className="relative aspect-square bg-surface-alt">
              {sl.preview && (sl.kind === 'video' && sl.file
                ? <video src={sl.preview} className="h-full w-full object-cover" muted onLoadedMetadata={(e) => setRatio(sl.id, e.currentTarget.videoWidth, e.currentTarget.videoHeight)} />
                : <img src={sl.preview} alt="" className="h-full w-full object-cover" onLoad={(e) => setRatio(sl.id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} />)}
              <span className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 text-[11px] font-semibold text-white">{i + 1}</span>
              {sl.kind === 'video' && <span className="absolute bottom-1.5 left-1.5 flex items-center gap-0.5 rounded bg-black/70 px-1 text-[10px] text-white"><Film size={10} />{seconds(sl.media?.durationMs) || 'Video'}</span>}
              {sl.key && <CheckCircle2 size={16} className="absolute right-1.5 top-1.5 text-success" aria-label="On X" />}
            </div>
            <div className="flex items-center justify-between gap-1 px-1.5 py-1">
              <span className="truncate text-[11px] text-text-muted" title={sl.file?.name ?? sl.media?.name}>
                {sl.ratio ? (Math.abs(sl.ratio - 1) < 0.05 ? '1:1' : Math.abs(sl.ratio - 1.91) < 0.1 ? '1.91:1' : Math.abs(sl.ratio - 16 / 9) < 0.06 ? '16:9' : `${sl.ratio.toFixed(2)}:1`) : ''}
              </span>
              <span className="flex items-center">
                <button type="button" className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30" aria-label={`Move card ${i + 1} left`} disabled={i === 0} onClick={() => moveSlide(sl.id, -1)}><ArrowLeft size={13} /></button>
                <button type="button" className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30" aria-label={`Move card ${i + 1} right`} disabled={i === slides.length - 1} onClick={() => moveSlide(sl.id, 1)}><ArrowRight size={13} /></button>
                <button type="button" className="p-1 text-text-muted hover:text-danger" aria-label={`Remove card ${i + 1}`} onClick={() => removeSlide(sl.id)}><Trash2 size={13} /></button>
              </span>
            </div>
          </li>)}
        </ol>}
        {mixedKinds && <p className="mt-2 text-xs text-danger">Use only images or only videos in one carousel.</p>}
        {!mixedKinds && mixedShapes && <p className="mt-2 text-xs text-danger">The cards have different shapes; X needs them all 1:1 or all 1.91:1.</p>}
      </FormField>}

      <FormField label={format === 'carousel' ? 'Destination' : 'Card'} htmlFor="xp-card"
        hint={format === 'carousel' ? 'Where every card of the carousel leads' : 'A card turns the image or video into a button'}>
        <XSelect id="xp-card" value={cardKind} onChange={(v) => v && setCardKind(v as CardKind)} options={cardOptions} />
      </FormField>
      {(cardKind === 'website' || cardKind === 'lead') && <div className="grid gap-x-4 sm:grid-cols-2">
        <FormField label="Card headline" htmlFor="xp-title" required hint={`${title.length}/70`}>
          <Input id="xp-title" maxLength={70} value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormField>
        {cardKind === 'website'
          ? <FormField label="Website" htmlFor="xp-url" required><Input id="xp-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" /></FormField>
          : <FormField label="Lead form" htmlFor="xp-form" required hint={leadForms?.length === 0 ? 'Create one under Tools' : undefined}>
              <XSelect id="xp-form" value={leadFormId} onChange={setLeadFormId} disabled={!leadForms?.length}
                placeholder={leadForms ? 'Choose a lead form' : 'Loading…'} options={(leadForms ?? []).map((f) => ({ value: f.id, label: f.name || f.id }))} />
            </FormField>}
      </div>}
      {cardKind === 'app' && <div className="grid gap-x-4 sm:grid-cols-2">
        <FormField label="App store" htmlFor="xp-store" required>
          <XSelect id="xp-store" value={appStore} onChange={(v) => { if (v) { setAppStore(v as 'ios' | 'android'); setAppId(''); } }}
            options={[{ value: 'ios', label: 'App Store (iPhone)' }, { value: 'android', label: 'Google Play (Android)' }]} />
        </FormField>
        <FormField label={appStore === 'ios' ? 'App Store id' : 'Google Play package name'} htmlFor="xp-app" required
          hint={appStore === 'ios' ? 'The number in the App Store link' : 'As in the Play Store link: id=…'}>
          <Input id="xp-app" inputMode={appStore === 'ios' ? 'numeric' : 'text'} placeholder={appStore === 'ios' ? '333903271' : 'com.company.app'}
            value={appId} onChange={(e) => setAppId(e.target.value)} />
        </FormField>
        <FormField label="Store country" htmlFor="xp-country" hint="2-letter code of the store page">
          <Input id="xp-country" maxLength={2} value={appCountry} onChange={(e) => setAppCountry(e.target.value.toUpperCase())} />
        </FormField>
        <FormField label="Button" htmlFor="xp-button">
          <XSelect id="xp-button" value={appButton} onChange={(v) => v && setAppButton(v as XAppButton)} options={APP_BUTTONS} />
        </FormField>
      </div>}
      {failed && <p role="alert" className="mb-3 flex items-start gap-1.5 text-sm text-danger"><XCircle size={15} className="mt-0.5 shrink-0" />{failed.part === 'media' ? (video ? 'Video upload' : 'Image upload') : failed.part === 'card' ? 'Card' : 'Post'} failed: {failed.message}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={submit} loading={!!busy} iconLeft={failed ? <RefreshCw size={14} /> : <Send size={14} />}>{failed ? 'Try again' : format === 'carousel' ? 'Create carousel post' : 'Create post'}</Button>
        {busy && progress && <span className="xa-muted">{progress}</span>}
        {steps.filter((st) => st.show && st.done).map((st, i) => <span key={`${st.part}-${i}`} className="flex items-center gap-1 text-xs text-success"><CheckCircle2 size={13} />{st.label}</span>)}
      </div>
    </div>

    <aside aria-label="Preview" className="mt-4 lg:mt-0">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Preview</p>
      <div className="rounded-xl border border-border bg-surface p-3 text-sm">
        <p className="whitespace-pre-wrap break-words">{text || <span className="text-text-muted">Your post text</span>}</p>
        {format === 'single' && hasMedia && <div className="mt-2 overflow-hidden rounded-lg border border-border">
          {preview && isVideoFile(file) && <video src={preview} className="max-h-48 w-full bg-black object-contain" muted controls />}
          {preview && !isVideoFile(file) && <img src={preview} alt="" className="max-h-48 w-full object-cover" />}
          {!preview && picked && <img src={isVideo(picked) ? picked.posterUrl : picked.mediaUrl} alt="" className="max-h-48 w-full object-cover" />}
          <CardFooter cardKind={cardKind} title={title} url={url} appId={appId} appStore={appStore} appButton={appButton} />
        </div>}
        {format === 'carousel' && slides.length > 0 && <div className="mt-2 flex snap-x gap-2 overflow-x-auto pb-1" aria-label="Carousel preview">
          {slides.map((sl) => <div key={sl.id} className="w-40 shrink-0 snap-start overflow-hidden rounded-lg border border-border">
            {!sl.preview ? <div className="aspect-square bg-surface-alt" />
              : sl.kind === 'video' && sl.file ? <video src={sl.preview} className="aspect-square w-full bg-black object-cover" muted />
              : <img src={sl.preview} alt="" className="aspect-square w-full bg-surface-alt object-cover" />}
            <CardFooter cardKind={cardKind} title={title} url={url} appId={appId} appStore={appStore} appButton={appButton} />
          </div>)}
        </div>}
        {busy && <Loader2 size={14} className="mt-2 animate-spin text-text-muted" />}
      </div>
      <p className="xa-muted mt-2">Posts made here are promoted-only: they never appear on your profile's timeline.</p>
    </aside>
  </div>;
}

/** The media library as a grid of thumbnails (videos show their still frame and length). */
function LibraryGrid({ library, isOn, onPick }: { library: XMedia[]; isOn: (m: XMedia) => boolean; onPick: (m: XMedia) => void }) {
  return <div className="mt-2 grid max-h-48 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
    {library.length === 0 && <p className="xa-muted col-span-full">The media library is empty.</p>}
    {library.filter((m) => m.mediaKey).map((m) => <button key={m.mediaKey} type="button" title={m.name || m.fileName || m.mediaKey}
      aria-pressed={isOn(m)} onClick={() => onPick(m)}
      className={`relative aspect-square overflow-hidden rounded-md border-2 ${isOn(m) ? 'border-primary' : 'border-transparent'}`}>
      {(isVideo(m) ? m.posterUrl : m.mediaUrl)
        ? <img src={isVideo(m) ? m.posterUrl : m.mediaUrl} alt={m.name || ''} className="h-full w-full object-cover" loading="lazy" />
        : <span className="text-[10px]">{m.name || m.mediaKey}</span>}
      {isVideo(m) && <span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-black/70 px-1 text-[10px] text-white"><Film size={10} />{seconds(m.durationMs)}</span>}
    </button>)}
  </div>;
}

/** The line under the media: website headline + domain, lead form, or the app button. */
function CardFooter({ cardKind, title, url, appId, appStore, appButton }: {
  cardKind: CardKind; title: string; url: string; appId: string; appStore: 'ios' | 'android'; appButton: XAppButton;
}) {
  if (cardKind === 'website' || cardKind === 'lead') {
    return <p className="border-t border-border px-3 py-2 text-xs font-medium">{title || 'Card headline'}
      <span className="block font-normal text-text-muted">{cardKind === 'website' ? url.replace(/^https?:\/\//, '') : 'Opens a lead form'}</span></p>;
  }
  if (cardKind === 'app') {
    return <p className="flex items-center justify-between border-t border-border px-3 py-2 text-xs font-medium">
      <span className="truncate">{appId || (appStore === 'ios' ? 'App Store app' : 'Google Play app')}</span>
      <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] text-on-primary">{APP_BUTTONS.find((x) => x.value === appButton)?.label}</span>
    </p>;
  }
  return null;
}
